/*
 * tw-otp-modal.js
 *
 * "Changed your mind?" upsell modal — shown when the customer clicks ATC while
 * One-Time Purchase is selected.
 *
 * Behaviour overview:
 *  - Intercepts ATC form submit when OTP is active.
 *  - Opens a <dialog> that mirrors the subscription options:
 *      Toggle OFF  → simple "Current plan: One Time Purchase | price" view.
 *      Toggle ON   → full subscription plan list + one-time option row.
 *  - Plan selection updates the modal price / badge; the active page tile radio
 *    is also checked silently so Recharge keeps selling_plan in sync.
 *  - Modal ATC calls /cart/add.js directly (no form re-submit needed):
 *      OTP  → { id, quantity: 1 }
 *      Sub  → subscription line only (GWP gifts are visual in cart)
 *  - On success dispatches `cart:refresh` (Shopify theme standard) and closes.
 *
 * Data source: data-* attributes on `.quantity-save-badge[data-option-index]`
 * elements. `data-variant-id`, `data-selling-plan-id`, and `data-input-id` are
 * added by tw-custom-main-product.liquid; other attributes were already present.
 *
 * Loaded only by tw-custom-main-product.liquid.
 */

(function () {
  'use strict';

  /* ─── Selectors ─────────────────────────────────────────────────────────── */

  var SEL = {
    modal:         '[data-tw-otp-modal]',
    close:         '[data-tw-otp-modal-close]',
    toggle:        '[data-tw-otp-modal-toggle]',
    saveBadge:     '[data-tw-otp-modal-save-badge]',
    priceRef:      '[data-tw-otp-modal-price-ref]',
    giftsBanner:   '[data-tw-otp-modal-gifts-banner]',
    subSection:    '[data-tw-otp-modal-sub-section]',
    planList:      '[data-tw-otp-modal-plan-list]',
    giftsNote:     '[data-tw-otp-modal-gifts-note]',
    otpLabel:      '[data-tw-otp-modal-otp-label]',
    otpPlan:       '[data-tw-otp-modal-otp-plan]',
    atcBtn:        '[data-tw-otp-modal-atc]',
    atcPrice:      '[data-tw-otp-modal-atc-price]',
    atcSpinner:    '[data-tw-otp-modal-spinner]',
    tileBadge:     '.quantity-save-badge[data-option-index]',
    sellingPlan:   'input[name="selling_plan"]',
    variantInput:  'input[name="id"]',
    rcPurchaseOpt: 'input[name="purchaseOption"]',
    widget:        'recharge-subscription-widget',
    giftsSource:   '#tw-rc-subscription-gifts-source',
  };

  /* ─── Module state ───────────────────────────────────────────────────────── */

  /** @type {HTMLDialogElement|null} */
  var _modal = null;

  /** @type {boolean} */
  var _isSub = false;

  /** @type {number} 0-based index into _tiles */
  var _selectedTileIdx = 0;

  /** @type {Array<TileData>} */
  var _tiles = [];

  /** @type {boolean} */
  var _atcInFlight = false;

  /* ─── Price display ─────────────────────────────────────────────────────── */

  /**
   * Strips trailing ISO currency codes (e.g. "£25.99 GBP" → "£25.99").
   *
   * @param {string|null|undefined} str
   * @returns {string}
   */
  function formatDisplayMoney(str) {
    if (str == null || str === '') return '';
    return String(str).replace(/\s+[A-Za-z]{3}\s*$/i, '').trim();
  }

  /* ─── Tile data ──────────────────────────────────────────────────────────── */

  /**
   * @typedef {Object} TileData
   * @property {number}  index          1-based option index
   * @property {string}  inputId        id of the page radio input for this tile
   * @property {string}  variantId
   * @property {string}  sellingPlanId  empty string if no plan
   * @property {string}  saveSub        subscription save % string e.g. "10"
   * @property {string}  saveOtp        one-time save % string
   * @property {string}  snsPrice       formatted subscription price e.g. "£23.39"
   * @property {string}  onetimePrice   formatted one-time price e.g. "£25.99"
   * @property {string}  compareAt      formatted compare-at price e.g. "£25.99"
   * @property {string}  titleText      e.g. "1 Bottle"
   * @property {string}  supplyText     e.g. "1 month supply"
   */

  /**
   * Reads all tile badges from the page and returns structured data.
   *
   * @returns {TileData[]}
   */
  function readTiles() {
    var nodes = document.querySelectorAll(SEL.tileBadge);
    var tiles = [];
    nodes.forEach(function (badge) {
      var label = badge.closest('label');
      var titleEl = label
        ? (label.querySelector('.option_selector_desc .tw-mb-unit-count') ||
          label.querySelector('.option_selector_desc__title'))
        : null;
      var supplyEl = label
        ? (label.querySelector('.option_selector_desc .tw-mb-supply-days') ||
          label.querySelector('.option_selector_desc__supply'))
        : null;

      tiles.push({
        index:        parseInt(badge.getAttribute('data-option-index') || '1', 10),
        inputId:      badge.getAttribute('data-input-id') || '',
        variantId:    badge.getAttribute('data-variant-id') || '',
        sellingPlanId: badge.getAttribute('data-selling-plan-id') || '',
        saveSub:      (badge.getAttribute('data-save-subscription') || '').trim(),
        saveOtp:      (badge.getAttribute('data-save-onetime') || '').trim(),
        snsPrice:     formatDisplayMoney(
          badge.getAttribute('data-sns-price') || ''
        ),
        onetimePrice: formatDisplayMoney(
          badge.getAttribute('data-onetime-price') || ''
        ),
        compareAt:    formatDisplayMoney(
          badge.getAttribute('data-compare-at-display') || ''
        ),
        titleText:    titleEl
          ? titleEl.textContent.replace(/\s+/g, ' ').trim()
          : '',
        supplyText:   supplyEl
          ? supplyEl.textContent.replace(/\s+/g, ' ').trim()
          : '',
      });
    });
    return tiles;
  }

  /* ─── Page state helpers ─────────────────────────────────────────────────── */

  /**
   * Returns true when the Recharge widget (or form) has a subscription active.
   *
   * @returns {boolean}
   */
  function isSubscriptionSelected() {
    var sp = document.querySelector(SEL.sellingPlan);
    if (sp) return sp.value !== '';

    var widget = document.querySelector(SEL.widget);
    if (widget && widget.shadowRoot) {
      var po = widget.shadowRoot.querySelector(SEL.rcPurchaseOpt + ':checked');
      if (po) return po.value === 'subscription';
    }
    return false;
  }

  /**
   * Returns 0-based index of currently checked quantity tile.
   *
   * @returns {number}
   */
  function getActiveTileIdx() {
    for (var i = 0; i < _tiles.length; i++) {
      var input = document.getElementById(_tiles[i].inputId);
      if (input && input.checked) return i;
    }
    return 0;
  }

  /* ─── Gifts banner helper ────────────────────────────────────────────────── */

  /**
   * Builds gifts banner HTML with "N Free gift(s)" at font-weight 700.
   *
   * @returns {string}
   */
  function getGiftsBannerHtml() {
    var tmpl = document.querySelector(SEL.giftsSource);
    if (!tmpl) return '';

    var banner = tmpl.content
      ? tmpl.content.querySelector('.tw-rc-subscription-gifts__banner')
      : tmpl.querySelector('.tw-rc-subscription-gifts__banner');
    if (!banner) return '';

    var leadEl = banner.querySelector('.tw-rc-subscription-gifts__lead');
    if (!leadEl) return '';

    var leadText = leadEl.textContent.replace(/\s+/g, ' ').trim();
    var match = leadText.match(/^(\d+\s+Free gifts?)(.*)$/i);
    var html = '';

    if (match) {
      html =
        '<span class="tw-otp-modal__gifts-banner-bold">' +
        escHtml(match[1]) +
        '</span>' +
        escHtml(match[2]);
    } else {
      html = escHtml(leadText);
    }

    var activeTile = _tiles.length ? _tiles[_selectedTileIdx] : null;
    var pct = activeTile ? (activeTile.saveSub || '').trim() : '';
    if (pct) {
      html += escHtml(' Save ' + pct + '%!');
    }

    return html;
  }

  /**
   * Counts gift product cards in the gifts source template.
   *
   * @returns {number}
   */
  function getGiftsCount() {
    var tmpl = document.querySelector(SEL.giftsSource);
    if (!tmpl) return 0;
    var cards = tmpl.content
      ? tmpl.content.querySelectorAll('.tw-rc-subscription-gifts__card')
      : tmpl.querySelectorAll('.tw-rc-subscription-gifts__card');
    return cards.length;
  }

  /**
   * Reads gift product titles from the gifts source template.
   *
   * @returns {string[]}
   */
  function getGiftTitles() {
    var tmpl = document.querySelector(SEL.giftsSource);
    if (!tmpl) return [];

    var nodes = tmpl.content
      ? tmpl.content.querySelectorAll('.tw-rc-subscription-gifts__title')
      : tmpl.querySelectorAll('.tw-rc-subscription-gifts__title');

    var titles = [];
    nodes.forEach(function (el) {
      var title = el.textContent.replace(/\s+/g, ' ').trim();
      if (title) titles.push(title);
    });
    return titles;
  }

  /**
   * Formats gift titles as "a foo, bar, and baz included."
   *
   * @param {string[]} titles
   * @returns {string}
   */
  function formatGiftTitlesList(titles) {
    if (!titles.length) return '';

    var names = titles.map(function (title) {
      return escHtml(title.toLowerCase());
    });

    if (names.length === 1) {
      return 'a ' + names[0] + ' included.';
    }

    if (names.length === 2) {
      return 'a ' + names[0] + ' and ' + names[1] + ' included.';
    }

    var last = names.pop();
    return 'a ' + names.join(', ') + ', and ' + last + ' included.';
  }

  /**
   * Builds gifts note HTML with bold count phrase and product title list.
   *
   * @param {number} count
   * @returns {string}
   */
  function getGiftsNoteHtml(count) {
    var giftsPhrase = count + ' free gift' + (count !== 1 ? 's' : '');
    var detail = formatGiftTitlesList(getGiftTitles());

    var html =
      'Receive <span class="tw-otp-modal__gifts-note-bold">' +
      escHtml(giftsPhrase) +
      '</span> with your order';

    if (detail) {
      html += ': ' + detail;
    } else {
      html += '.';
    }

    return html;
  }

  /* ─── DOM helpers ────────────────────────────────────────────────────────── */

  /**
   * @param {string} selector
   * @returns {Element|null}
   */
  function q(selector) {
    return _modal ? _modal.querySelector(selector) : null;
  }

  /**
   * Toggles the `hidden` attribute on an element.
   *
   * @param {Element|null} el
   * @param {boolean} show
   */
  function setVisible(el, show) {
    if (!el) return;
    if (show) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  /* ─── Plan option rendering ──────────────────────────────────────────────── */

  /**
   * Builds a single subscription plan row element.
   *
   * @param {TileData} tile
   * @param {boolean}  checked
   * @param {number}   idx 0-based
   * @returns {HTMLElement}
   */
  function buildSubPlanItem(tile, checked, idx) {
    var id = 'tw-otp-plan-' + idx;
    var li = document.createElement('div');
    li.className = 'tw-otp-modal__plan-item';
    li.setAttribute('data-tw-otp-plan-idx', String(idx));

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = 'tw-otp-plan';
    input.id = id;
    input.value = String(idx);
    input.checked = checked;
    input.className = 'tw-otp-modal__plan-radio';

    var label = document.createElement('label');
    label.htmlFor = id;
    label.className = 'tw-otp-modal__plan-label';

    var nameWrap = document.createElement('span');
    nameWrap.className = 'tw-otp-modal__plan-name';

    var baseTitle = tile.titleText || ('Option ' + tile.index);
    var supplyShort = tile.supplyText
      ? tile.supplyText.replace(/\bsupply\b/i, '').trim()
      : '';

    var titleSpan = document.createElement('span');
    titleSpan.className = 'tw-otp-modal__plan-title';
    titleSpan.textContent = baseTitle;
    nameWrap.appendChild(titleSpan);

    if (supplyShort) {
      var supplySpan = document.createElement('span');
      supplySpan.className = 'tw-otp-modal__plan-supply';
      supplySpan.textContent = ' (' + supplyShort + ')';
      nameWrap.appendChild(supplySpan);
    }

    var infoWrap = document.createElement('span');
    infoWrap.className = 'tw-otp-modal__plan-info';
    infoWrap.appendChild(nameWrap);

    if (tile.saveSub) {
      var badge = document.createElement('span');
      badge.className = 'tw-otp-modal__plan-badge';
      badge.textContent = 'SAVE ' + tile.saveSub + '%';
      infoWrap.appendChild(badge);
    }

    var priceWrap = document.createElement('span');
    priceWrap.className = 'tw-otp-modal__plan-prices';

    if (tile.compareAt && tile.saveSub) {
      var compare = document.createElement('s');
      compare.className = 'tw-otp-modal__plan-compare';
      compare.textContent = tile.compareAt;
      priceWrap.appendChild(compare);
    }

    if (tile.snsPrice) {
      var price = document.createElement('span');
      price.className = 'tw-otp-modal__plan-price';
      price.textContent = tile.snsPrice;
      priceWrap.appendChild(price);
    }

    label.appendChild(infoWrap);
    label.appendChild(priceWrap);
    li.appendChild(input);
    li.appendChild(label);

    return li;
  }

  /**
   * Builds the one-time purchase row element.
   *
   * @param {TileData} tile — currently selected tile
   * @returns {HTMLElement}
   */
  function buildOtpPlanItem(tile) {
    var div = document.createElement('div');
    div.className = 'tw-otp-modal__otp-plan-item';

    var label = document.createElement('span');
    label.className = 'tw-otp-modal__otp-plan-name';
    label.textContent = 'One Time Purchase';

    var price = document.createElement('span');
    price.className = 'tw-otp-modal__otp-plan-price';
    price.textContent = tile ? tile.onetimePrice : '';

    div.appendChild(label);
    div.appendChild(price);
    return div;
  }

  /* ─── Modal render ───────────────────────────────────────────────────────── */

  /**
   * Populates/updates all dynamic parts of the modal from current state.
   * Called on open and on every toggle/plan-select.
   */
  function renderModal() {
    if (!_modal || !_tiles.length) return;

    var activeTile = _tiles[_selectedTileIdx];
    if (!activeTile) return;

    /* ── Save badge — always shows subscription saving % ── */
    var badgeEl = q(SEL.saveBadge);
    if (badgeEl) {
      var savePct = activeTile.saveSub;
      badgeEl.textContent = savePct ? 'SAVE ' + savePct + '%' : '';
      setVisible(badgeEl, !!savePct);
    }

    /* ── Price reference row ── */
    /*
     * Always shows the subscription saving incentive:
     *   compare-at (strike) → subscription price
     * In both OTP and sub states the reference is the same so the customer
     * can see what they'd save by subscribing.
     */
    var priceRef = q(SEL.priceRef);
    if (priceRef) {
      var priceHtml = '';
      if (activeTile.compareAt) {
        priceHtml += '<s class="tw-otp-modal__ref-compare">' +
          escHtml(activeTile.compareAt) + '</s>';
      }
      if (activeTile.snsPrice) {
        priceHtml += '<span class="tw-otp-modal__ref-current">' +
          escHtml(activeTile.snsPrice) + '</span>';
      }
      priceRef.innerHTML = priceHtml;
    }

    /* ── Gifts banner — shown whenever gifts exist, in both states ── */
    var giftsEl = q(SEL.giftsBanner);
    if (giftsEl) {
      var giftsHtml = getGiftsBannerHtml();
      if (giftsHtml) giftsEl.innerHTML = giftsHtml;
      setVisible(giftsEl, !!giftsHtml);
    }

    /* ── Toggle aria state + modal sub class (drives OTP border colour) ── */
    var toggleEl = q(SEL.toggle);
    if (toggleEl) {
      toggleEl.setAttribute('aria-checked', _isSub ? 'true' : 'false');
      toggleEl.classList.toggle('is-checked', _isSub);
    }
    _modal.classList.toggle('tw-otp-modal--sub', _isSub);

    /* ── Subscription section ── */
    var subSection = q(SEL.subSection);
    setVisible(subSection, _isSub);

    /* Always update gifts note visibility so toggling off hides it */
    var giftsNote = q(SEL.giftsNote);
    var count = getGiftsCount();
    if (giftsNote) {
      if (_isSub && count > 0) {
        giftsNote.innerHTML = getGiftsNoteHtml(count);
        setVisible(giftsNote, true);
      } else {
        setVisible(giftsNote, false);
      }
    }

    if (_isSub) {
      /* Plan list */
      var planList = q(SEL.planList);
      if (planList) {
        planList.innerHTML = '';
        _tiles.forEach(function (tile, idx) {
          planList.appendChild(buildSubPlanItem(tile, idx === _selectedTileIdx, idx));
        });
      }
    }

    /* ── One-time section ── */
    var otpLabel = q(SEL.otpLabel);
    if (otpLabel) {
      otpLabel.textContent = 'Current Plan';
      /* CSS text-transform: uppercase handles visual casing */
    }

    var otpPlan = q(SEL.otpPlan);
    if (otpPlan) {
      otpPlan.innerHTML = '';
      otpPlan.appendChild(buildOtpPlanItem(activeTile));
    }

    /* ── ATC button price ── */
    var atcPrice = q(SEL.atcPrice);
    if (atcPrice) {
      atcPrice.textContent = _isSub ? activeTile.snsPrice : activeTile.onetimePrice;
    }
  }

  /* ─── Open / close ───────────────────────────────────────────────────────── */

  /**
   * Opens the modal, re-reads tile data, sets initial state.
   */
  function openModal() {
    _modal = document.querySelector(SEL.modal);
    if (!_modal) return;

    _tiles = readTiles();
    if (!_tiles.length) return;

    _isSub = false;
    _selectedTileIdx = getActiveTileIdx();

    renderModal();

    _modal.showModal ? _modal.showModal() : _modal.setAttribute('open', '');
    document.body.classList.add('tw-otp-modal-open');
  }

  /**
   * Closes the modal and cleans up.
   */
  function closeModal() {
    if (!_modal) return;
    _modal.close ? _modal.close() : _modal.removeAttribute('open');
    document.body.classList.remove('tw-otp-modal-open');
  }

  /* ─── Tile sync ──────────────────────────────────────────────────────────── */

  /**
   * Silently selects the corresponding page tile radio (so Recharge updates
   * its selling_plan input). Does NOT scroll or animate — user stays on modal.
   *
   * @param {number} idx 0-based
   */
  function syncPageTile(idx) {
    var tile = _tiles[idx];
    if (!tile || !tile.inputId) return;

    var input = document.getElementById(tile.inputId);
    if (!input || input.checked) return;

    input.checked = true;

    /* Fire change so variants.js + Recharge pick it up */
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ─── Recharge subscription toggle ──────────────────────────────────────── */

  /**
   * Clicks the Recharge subscription radio button in the widget shadow root.
   * Safe to call when subscription is already selected.
   */
  function activateRechargeSubscription() {
    var widget = document.querySelector(SEL.widget);
    if (!widget || !widget.shadowRoot) return;

    var subInput = widget.shadowRoot.querySelector(
      'input[name="purchaseOption"][value="subscription"]'
    );
    if (subInput && !subInput.checked) {
      subInput.click();
    }
  }

  /**
   * Clicks the Recharge one-time radio button.
   */
  function activateRechargeOnetime() {
    var widget = document.querySelector(SEL.widget);
    if (!widget || !widget.shadowRoot) return;

    var otpInput = widget.shadowRoot.querySelector(
      'input[name="purchaseOption"][value="onetime"]'
    );
    if (otpInput && !otpInput.checked) {
      otpInput.click();
    }
  }

  /* ─── Cart add ───────────────────────────────────────────────────────────── */

  /**
   * Parses cart API responses safely (HTML error pages won't throw SyntaxError).
   *
   * @param {Response} res
   * @returns {Promise<object>}
   */
  function parseCartApiResponse(res) {
    return res.text().then(function (text) {
      var contentType = (res.headers.get('content-type') || '').toLowerCase();
      var looksJson =
        contentType.indexOf('application/json') !== -1 ||
        contentType.indexOf('text/javascript') !== -1 ||
        contentType.indexOf('application/javascript') !== -1;

      if (!looksJson) {
        if (res.status === 429) {
          throw new Error(
            'Too many cart requests. Please wait a moment and try again.',
          );
        }
        throw new Error('Could not add item to cart.');
      }

      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error('Could not add item to cart.');
      }
    });
  }

  /**
   * Builds /cart/add.js body for OTP or subscription (parent line only).
   *
   * @param {TileData} tile
   * @param {boolean} isSub
   * @returns {object|null} null when payload is invalid
   */
  function buildCartAddBody(tile, isSub) {
    var variantId = parseInt(tile.variantId, 10);
    if (!variantId || isNaN(variantId)) return null;

    var sections = getCartSectionIds();
    var sectionsUrl = window.location.pathname;
    var body = {
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: sectionsUrl,
    };

    if (!isSub) {
      return body;
    }

    if (!tile.sellingPlanId) {
      return null;
    }

    var planNum = parseInt(tile.sellingPlanId, 10);
    if (isNaN(planNum)) return null;

    body.selling_plan = planNum;
    return body;
  }

  /* ─── Cart drawer helpers ────────────────────────────────────────────────── */

  /**
   * Returns the `cart-drawer` custom element, or null if the theme uses a
   * different cart experience (page / mini-cart).
   *
   * @returns {HTMLElement|null}
   */
  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  /**
   * Comma-separated section IDs for /cart/add.js section rendering.
   *
   * @returns {string}
   */
  function getCartSectionIds() {
    var drawer = getCartDrawer();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer
        .getSectionsToRender()
        .map(function (s) {
          return s.section || s.id;
        })
        .filter(Boolean)
        .join(',');
    }
    return 'cart-drawer,cart-icon-bubble';
  }

  /**
   * Hands the successful /cart/add.js JSON response off to the cart drawer so
   * it can re-render and open itself — identical to product-form.js behaviour.
   *
   * @param {object} response — parsed JSON from /cart/add.js (sections included)
   */
  /**
   * @param {string} html
   * @param {string} selector
   * @returns {string}
   */
  function getSectionInnerHTML(html, selector) {
    if (!html) return '';
    var el = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /**
   * Manual section HTML swap when renderContents throws (missing DOM node).
   *
   * @param {object} sections
   */
  function patchCartSections(sections) {
    var cartDrawerEl = document.getElementById('CartDrawer');
    if (cartDrawerEl && sections['cart-drawer']) {
      var drawerInner = cartDrawerEl.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.innerHTML =
          getSectionInnerHTML(sections['cart-drawer'], '.drawer__inner') ||
          drawerInner.innerHTML;
      }
    }

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      bubble.innerHTML =
        getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section') ||
        bubble.innerHTML;
    }
  }

  /**
   * @param {object} response
   */
  function openCartDrawer(response) {
    var drawer = getCartDrawer();
    var sections = response && response.sections;

    if (!sections) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    if (drawer && drawer.classList) {
      drawer.classList.remove('is-empty');
    }

    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(response, true);
        return;
      } catch (err) {
        console.warn('[tw-otp-modal] renderContents failed, using fallback:', err);
      }
    }

    patchCartSections(sections);
    if (drawer && typeof drawer.toggleDrawerClasses === 'function') {
      drawer.toggleDrawerClasses();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /**
   * Adds the selected plan to cart via fetch, updates the cart drawer, then
   * closes the modal.
   */
  function handleAtcClick() {
    if (_atcInFlight) return;

    var tile = _tiles[_selectedTileIdx];
    if (!tile) return;

    var atcBtn = q(SEL.atcBtn);
    var spinner = q(SEL.atcSpinner);
    var atcCta  = atcBtn ? atcBtn.querySelector('.tw-otp-modal__atc-cta') : null;

    _atcInFlight = true;
    if (atcBtn) atcBtn.disabled = true;
    setVisible(spinner, true);
    if (atcCta) atcCta.setAttribute('aria-hidden', 'true');

    var body = buildCartAddBody(tile, _isSub);
    if (!body) {
      _atcInFlight = false;
      alert('Invalid product selection. Please try again.');
      if (atcBtn) atcBtn.disabled = false;
      setVisible(spinner, false);
      if (atcCta) atcCta.removeAttribute('aria-hidden');
      return;
    }

    var cartAddUrl =
      window.routes && window.routes.cart_add_url
        ? window.routes.cart_add_url
        : '/cart/add.js';

    var addPromise = fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      return parseCartApiResponse(res).then(function (data) {
        if (!res.ok) {
          throw new Error(
            data.description ||
              data.message ||
              'Could not add item to cart.',
          );
        }
        return data;
      });
    });

    addPromise
      .then(function (response) {
        if (response.status) {
          throw new Error(
            response.description ||
              response.message ||
              'Could not add item to cart.'
          );
        }
        closeModal();
        openCartDrawer(response);
      })
      .catch(function (err) {
        console.error('[tw-otp-modal] Cart add error:', err);
        alert(err.message || 'Something went wrong. Please try again.');
      })
      .finally(function () {
        _atcInFlight = false;
        if (atcBtn) atcBtn.disabled = false;
        setVisible(spinner, false);
        if (atcCta) atcCta.removeAttribute('aria-hidden');
      });
  }

  /* ─── Event handlers ─────────────────────────────────────────────────────── */

  /**
   * Handles toggle button click — switches between OTP and Sub state.
   */
  function handleToggle() {
    _isSub = !_isSub;

    if (_isSub) {
      activateRechargeSubscription();
      syncPageTile(_selectedTileIdx);
    } else {
      activateRechargeOnetime();
    }

    renderModal();
  }

  /**
   * Handles plan radio selection within the modal plan list.
   *
   * @param {Event} e
   */
  function handlePlanSelect(e) {
    var item = e.target.closest('[data-tw-otp-plan-idx]');
    if (!item) return;

    var radio = item.querySelector('.tw-otp-modal__plan-radio');
    if (!radio) return;

    var idx = parseInt(radio.value, 10);
    if (isNaN(idx) || idx === _selectedTileIdx) return;

    radio.checked = true;
    _selectedTileIdx = idx;
    syncPageTile(idx);
    renderModal();
  }

  /**
   * Intercepts the main ATC form submit.
   * Opens the modal when OTP is selected; lets the form proceed if already sub.
   *
   * @param {SubmitEvent|MouseEvent} e
   */
  function handleAtcSubmit(e) {
    /* Only intercept when one-time is selected */
    if (isSubscriptionSelected()) return;

    /* Prevent the product-form custom element from submitting */
    e.preventDefault();
    e.stopImmediatePropagation();

    openModal();
  }

  /* ─── Keyboard handling ──────────────────────────────────────────────────── */

  /**
   * Closes modal on Escape key (browsers also close <dialog> natively; this
   * handles cases where showModal() isn't supported).
   *
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (e.key === 'Escape') closeModal();
  }

  /* ─── Utility ────────────────────────────────────────────────────────────── */

  /**
   * Minimal HTML escaper for text injected via innerHTML.
   *
   * @param {string} str
   * @returns {string}
   */
  function escHtml(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ─── Binding ────────────────────────────────────────────────────────────── */

  /**
   * Attaches all event listeners to the modal and the ATC buttons.
   */
  function bindEvents() {
    var modal = document.querySelector(SEL.modal);
    if (!modal) return;

    /* Modal-level delegation */
    modal.addEventListener('click', function (e) {
      /* Close: backdrop or X button */
      if (e.target.closest(SEL.close)) {
        closeModal();
        return;
      }
      /* Toggle */
      if (e.target.closest(SEL.toggle)) {
        handleToggle();
        return;
      }
      /* ATC */
      if (e.target.closest(SEL.atcBtn)) {
        handleAtcClick();
        return;
      }
      /* Sub plan radio */
      if (e.target.closest(SEL.planList)) {
        handlePlanSelect(e);
      }
    });

    /* Native dialog cancel (Escape) */
    modal.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeModal();
    });

    document.addEventListener('keydown', handleKeydown);

    /* Intercept ATC submit — product-form fires a submit on its inner <form> */
    document.querySelectorAll('product-form[data-tw-atc-inline]').forEach(function (pf) {
      var form = pf.querySelector('form[data-type="add-to-cart-form"]');
      if (form && !form.__twOtpModalBound) {
        form.__twOtpModalBound = true;
        /*
         * `capture: true` so we run before product-form's own submit listener
         * which calls /cart/add.js and closes the loading state immediately.
         */
        form.addEventListener('submit', handleAtcSubmit, true);
      }
    });

    /* Also cover the main ATC button click directly (some themes prevent submit) */
    document.querySelectorAll('product-form[data-tw-atc-inline] [name="add"]').forEach(function (btn) {
      if (!btn.__twOtpModalBound) {
        btn.__twOtpModalBound = true;
        btn.addEventListener('click', function (e) {
          if (isSubscriptionSelected()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          openModal();
        }, true);
      }
    });
  }

  /* ─── Boot ───────────────────────────────────────────────────────────────── */

  function boot() {
    if (!document.querySelector(SEL.modal)) return;
    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
